import { isPlatformBrowser } from '@angular/common';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import {
  OauthAEMConfig,
  UserDataService,
  UserSessionService
} from '@com-delta-idp/mnx-oauth';
import { LoggingService } from '@com-delta-idp/mnx-utils';
import { Observable, Subscription, of } from 'rxjs';
import { map, share } from 'rxjs/operators';
import { CookieHelper } from '../../common/cookie-helper';
import { ApplicationConfig } from '../../config/config';
import { TrackerDataService } from '../acctactvty/trackerData.service';
import { CommonService } from '../common.service';

@Injectable({
  providedIn: 'root'
})
export class LogoutService {
  authConfig: OauthAEMConfig;
  private data: any;
  private observableData: Observable<any>;
  refreshSessionDashboardSubscribe: Subscription;

  constructor(
    private http: HttpClient,
    private config: ApplicationConfig,
    private cookieHelper: CookieHelper,
    private commonService: CommonService,
    private userSession: UserSessionService,
    private userData: UserDataService,
    private trackService: TrackerDataService,
    private loggingService: LoggingService,
    @Inject(PLATFORM_ID) private platformId: Object
  ) {}

  logOutAction(logOutUrl: string): Observable<any> {
    this.removeCookieDuringLogout();
    if (this.data) {
      return of(this.data);
    } else if (this.observableData) {
      return this.observableData;
    } else {
      const customerHeader = new HttpHeaders({
        'Content-type': 'application/json; charset=utf-8'
      });
      customerHeader.append('Accept', 'application/json');
      this.observableData = this.http
        .post(
          `${logOutUrl}`,
          {},
          { headers: customerHeader, responseType: 'json' }
        )
        .pipe(
          map((response) => {
            this.observableData = null;
            this.data = response;
            return this.data;
          }),
          share()
        );
      return this.observableData;
    }
  }

  async removeCookieDuringLogout() {
    this.cookieHelper.setCookieName('isHipmunk', '', 0, false);
    this.cookieHelper.setCookieName('account', '', 0, false);
    this.cookieHelper.setCookieName('accountType', '', 0, false);
    this.cookieHelper.setCookieName('restrictToPersonal', '', 0, false);
    if (this.cookieHelper.getCookieValue('PF')) {
      this.cookieHelper.deleteCookie('PF');
    }
    if (this.cookieHelper.getCookieValue('PF.PERSISTENT')) {
      this.cookieHelper.deleteCookie('PF.PERSISTENT');
    }
    if (this.cookieHelper.getCookieValue('prompt')) {
      this.cookieHelper.deleteCookie('prompt');
    }
    await this.userData
      .getAuthConfigContent()
      .subscribe(async (response: OauthAEMConfig) => {
        if (response.ENABLE_NEW_PERSISTENT_LOGIN) {
          await this.userSession.deleteValuesFromServiceWorker();
          await this.userSession.deleteStatusValuesFromServiceWorker();
        }
      });
  }

  async pingLogOut(
    redirectToCurrentUrl?: boolean,
    differentRedirectUrl?: string
  ) {
    await this.getAuthConfig();
    let pingLogoutUrl = '';

    if (
      this.authConfig &&
      this.authConfig.ISSUER_PROD &&
      this.authConfig.LOG_OUT_URL &&
      this.authConfig.ENABLE_PING_LOGOUT
    ) {
      pingLogoutUrl = `${this.authConfig.ISSUER_PROD}${this.authConfig.LOG_OUT_URL}`;
    } else {
      window.location.replace('/');
    }

    localStorage.removeItem('encyToken');
    localStorage.removeItem('uid');

    if (redirectToCurrentUrl) {
      window.location.replace(`${pingLogoutUrl}${window.location.href}`);
    } else if (
      differentRedirectUrl &&
      differentRedirectUrl.startsWith(window.location.origin)
    ) {
      window.location.replace(`${pingLogoutUrl}${differentRedirectUrl}`);
    } else {
      window.location.replace(`${pingLogoutUrl}${window.location.origin}`);
    }
  }

  redirectLogOut(
    pingLogOutUrl: string,
    hostUrl: string,
    refreshUrl?: string,
    fullURL?: boolean
  ) {
    let pingUrlCheck = `${this.config.get('APP_PING_LOGOUT_URL')}`;
    if (fullURL) {
      window.location.replace(`${pingUrlCheck}${refreshUrl}`);
      return;
    }
    if (pingUrlCheck.startsWith('/')) {
      pingUrlCheck = pingUrlCheck.substring(1);
    }
    if (!refreshUrl && pingLogOutUrl) {
      pingUrlCheck = pingLogOutUrl;
    } else {
      pingUrlCheck += hostUrl;
    }
    window.location.replace(pingUrlCheck);
  }

  /**
   * Get the current window object
   */
  getWindow(): Window {
    if (isPlatformBrowser(this.platformId)) {
      return window;
    }
  }

  async getAuthConfig() {
    await this.commonService.getConfigFromAem().then((data) => {
      if (data) {
        this.authConfig = data;
      }
    });
  }

  logOut(differentRedirectUrl?: string, redirectToCurrentUrl?: boolean) {
    if (isPlatformBrowser(this.platformId) && document) {
      let logoutUrl = '';
      logoutUrl = this.config.get('APP_NEW_LOGOUT_URL');
      this.logOutAction(logoutUrl)
        .pipe()
        .subscribe(
          (res) => {
            this.pingLogOut(redirectToCurrentUrl, differentRedirectUrl);
          },
          (err) => {
            window.location.replace('/');
            this.loggingService.log('Caught Error: ' + err);
            console.error('Error in logOut method ' + differentRedirectUrl);
          }
        );
    }
  }

  keepMeLoggedIn() {
    if (isPlatformBrowser(this.platformId)) {
      this.userData.getAuthConfigContent().subscribe((response) => {
        if (response.ENABLE_NEW_PERSISTENT_LOGIN) {
          return this.userSession.getUserSession(true).subscribe();
        } else {
          this.keepMeLoggedInHelper();
        }
      });
    }
  }

  keepMeLoggedInHelper() {
    return this.trackService.refreshSessionDashboard().subscribe();
  }
}
